import { get } from 'lodash-es';
import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { makeStyles } from 'tss-react/mui';
import { green, grey, lightBlue } from '@mui/material/colors';
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useAppContext } from '../../contexts/app';
import { updateEventStatusMutation, eventQuery } from '../../apollo/events';
import JoinToggleMessage from './join-toggle-message';
import type { Event } from '../../types/Event';
import { eventPath } from '../../utils/slugify';
import type { EventInvite } from '../../types/Invite';

const useStyles = makeStyles()(() => ({
  goingSelected: {
    backgroundColor: `${green[100]} !important`,
  },
  interestedSelected: {
    backgroundColor: `${lightBlue[50]} !important`,
  },
  notGoingSelected: {
    backgroundColor: `${grey[200]} !important`,
  }
}));

type Props = {
  event?: Event;
  eventInvite?: EventInvite;
  onJoin?: (...args: unknown[]) => void;
  autoRsvp?: string;
  onRsvp?: (...args: unknown[]) => void;
};

const EventJoinToggle = ({ event, autoRsvp, onJoin, onRsvp, eventInvite }: Props) => {
  const { isLoggedIn, login } = useAppContext();
  const { classes } = useStyles();
  const [updateEventStatus, { loading: mutating }] = useMutation(updateEventStatusMutation, {
    update: (proxy, { data: { updateEventStatus: membership } }) => {
      // update event query w/ updated membership object
      try {
        const eventQueryVariables = eventInvite ? {
          id: event.id,
          inviteToken: eventInvite.id
        } : {
          id: event.id
        };
        const data = proxy.readQuery({
          query: eventQuery,
          variables: eventQueryVariables,
        });
        proxy.writeQuery({
          query: eventQuery,
          variables: eventQueryVariables,
          data: {
            ...data,
            event: {
              ...data.event,
              membership
            }
          }
        });
      } catch {}
    }
  });
  const submitting = mutating;
  const [autoRsvped, setAutoJoined] = useState(false);
  const status = get(event.membership, 'status');
  const disabled = submitting || event.status !== 'PUBLISHED' || !event.permissions.joinable;

  useEffect(() => {
    if (autoRsvp && !autoRsvped && !disabled) {
      updateEventStatus({
        variables: {
          eventId: event.id,
          status: autoRsvp,
          inviteToken: get(eventInvite, 'id')
        }
      })
        .then(() => {
          setAutoJoined(true);
          if (onRsvp) {
            onRsvp();
          }
        })
        .catch(() => {
          alert('RSVP Failed');
          setAutoJoined(true);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally fires only on isLoggedIn change for one-shot auto-RSVP
  }, [isLoggedIn]);

  return (
    <div>
      <ToggleButtonGroup
        value={status}
        sx={{
          backgroundColor: 'white',
          '& .MuiToggleButtonGroup-firstButton': {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          },
          '& .MuiToggleButtonGroup-middleButton': {
            borderRadius: 0,
          },
          '& .MuiToggleButtonGroup-lastButton': {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          },
          '& .MuiToggleButtonGroup-middleButton.Mui-selected, & .MuiToggleButtonGroup-lastButton.Mui-selected': {
            borderLeft: '1px solid',
            borderLeftColor: 'divider',
          },
        }}
        onChange={(e, newStatus) => {
          if (!isLoggedIn) {
            const queryParams = [];
            if (newStatus) {
              queryParams.push(`rsvp=${newStatus}`);
            }
            if (eventInvite) {
              queryParams.push(`join=${eventInvite.id}`);
            }
            const query = queryParams.length ? `?${queryParams.join('&')}` : '';
            login(`${eventPath(event.id, event.name)}${query}`);
            return;
          }

          if (!newStatus) {
            return;
          }

          updateEventStatus({
            variables: {
              eventId: event.id,
              status: newStatus,
              inviteToken: get(eventInvite, 'id')
            }
          })
            .then(() => {
              if (onRsvp) {
                onRsvp();
              }
            })
            .catch(() => {
              alert('RSVP Failed');
            });
        }}
        exclusive
      >
        <ToggleButton
          disabled={disabled}
          key={1}
          value="GOING"
          classes={{
            selected: classes.goingSelected
          }}
        >
          Going
        </ToggleButton>
        <ToggleButton
          disabled={disabled}
          key={2}
          value="INTERESTED"
          classes={{
            selected: classes.interestedSelected
          }}
        >
          Interested
        </ToggleButton>
        <ToggleButton
          disabled={disabled}
          key={3}
          value="NOT_GOING"
          classes={{
            selected: classes.notGoingSelected
          }}
        >
          Not Going
        </ToggleButton>
      </ToggleButtonGroup>

      <JoinToggleMessage
        event={event}
        eventInvite={eventInvite}
        autoRsvp={autoRsvp}
        onJoin={onJoin}
      />
    </div>
  );
};

export default EventJoinToggle;
